import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, ModalFooter } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import FormInput from '../../../../../../../../../../../../../../components/form/FormInput';
import ButtonGroup from '../../../../../../../../../../../../../../components/Modal/ModalButtonGroups/ButtonGroup';
import { analyticsIds } from '../../../../../../../../../../../../../../constants/analyticsIds';
import { ChakraSizes } from '../../../../../../../../../../../../../../constants/chakraSizes';
import { getValidationObject } from '../../../../../../../../../../../../../../constants/regexPatterns';
import {
  previewEmail,
  selectCurrentMessageHtml,
  selectCurrentSignature,
  selectCurrentSubjectHtml,
  selectRecordRecipients,
  sendEmail,
  updateInsuredRecipients
} from '../../../../../../../../../../../../../../redux/modules/emails';
import { hideModal, showModal } from '../../../../../../../../../../../../../../redux/modules/modal';
import { useGetPolicyAgent } from '../../../../../../../../../../../../../../services/policy-service';
import EmailBody from '../../../../../../../../../../../../../EmailEditorPage/components/EmailComponents/EmailBody';
import EmailSignature from '../../../../../../../../../../../../../EmailEditorPage/components/EmailComponents/EmailSignature';
import PreviewModal from '../../../../../../../../../../../../../EmailEditorPage/components/PreviewModal';

const EmailForm = ({ insuredId, recordId, jobId, policyId }) => {
  const dispatch = useDispatch();
  const emailSubject = useSelector(selectCurrentSubjectHtml);
  const emailBody = useSelector(selectCurrentMessageHtml);
  const emailSignature = useSelector(selectCurrentSignature);
  const emailRecipients = useSelector(selectRecordRecipients(recordId));

  const { data: policyAgentData } = useGetPolicyAgent(policyId, { enabled: !!policyId });

  const useFormMethods = useForm({
    defaultValues: {
      email_recipients: emailRecipients?.length
        ? emailRecipients.map(r => r.email).join(',')
        : policyAgentData?.agent?.email ?? ''
    }
  });

  const { handleSubmit, getValues } = useFormMethods;

  const refreshRecipients = () => {
    dispatch(updateInsuredRecipients(insuredId, getValues()?.email_recipients
      .split(',')
      .map(email => ({ email: email.trim() }))));
  };

  const onSubmit = async data => {
    refreshRecipients();

    const emailData = Object.assign(data, {
      email_body: `${emailBody}<br />${emailSignature}`,
      email_subject: emailSubject,
      insured_id: insuredId,
      job_id: jobId,
      record_id: recordId
    });

    if (policyAgentData?.agent?.id) {
      emailData.agent_id = policyAgentData?.agent?.id;
    }

    const onSuccess = () => dispatch(hideModal());

    dispatch(sendEmail(emailData, onSuccess));
  };

  const handlePreviewEmail = () => {
    refreshRecipients();

    dispatch(previewEmail(recordId, insuredId, jobId, policyAgentData?.agent?.id || null));

    dispatch(showModal(
      PreviewModal,
      {
        title: 'Preview',
        actionRequired: false,
        size: ChakraSizes.ExtraLarge,
        maxW: 1440,
        withHistory: true,
        center: false,
        buttons: {
          component: ButtonGroup,
          componentOptions: { justify: 'flex-end' },
          primaryButtonOptions: {
            onClick: handleSubmit(onSubmit),
            label: 'Send'
          },
          withHistoryButton: true
        }
      }
    ));
  };

  return (
    <>
      <form>
        <Box mb={8}>
          <FormInput
            inputName="email_recipients"
            labelName="Recipients"
            mb={4}
            placeholder="Recipients (Comma Separated)"
            useFormMethods={useFormMethods}
            validationRules={({
              required: 'Recipient Email is required',
              ...getValidationObject('commaSeparatedEmailAddress')
            })}
          />
        </Box>

        <Box
          backgroundColor="greyScale.200"
          borderBottomRadius="5px"
          borderTopRadius="5px"
          boxShadow="md"
        >
          <EmailBody
            bodyContainerId="email-body-editor-insured"
            borderTopRadius="5px"
            selectCodeType="popover"
            subjectContainerId="subject-editor-insured"
          />

          <Box py={2}>
            <hr />
          </Box>

          <EmailSignature
            backgroundColor="greyScale.200"
            borderBottomRadius="5px"
            signatureContainerId="signature-editor"
          />
        </Box>

      </form>
      <ModalFooter>

        <ButtonGroup
          dataAnalyticsId={analyticsIds.insuredProfilePage.policiesComponent.emailAgentModal.sendEmailButton}
          label="Send Email"
          secondaryClick={handleSubmit(handlePreviewEmail)}
          secondaryDataAnalyticsId={analyticsIds.insuredProfilePage.policiesComponent.emailAgentModal.previewEmailButton}
          secondaryLabel="Preview Email"
          size="lg"
          width="140px"
          onClick={handleSubmit(onSubmit)}
        />
      </ModalFooter>
    </>
  );
};

EmailForm.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  policyId: PropTypes.number
};

export default EmailForm;
